// 批次 23 Wave 2（#25，Gitea issue #25）：批次發送表單（form-plan-form-batch-send，origin
// form/planFormBatchSend.jsp 818 行）。三步精靈：
//   1. 查詢表單（:571-645：8 個 required select＋hidden phaseStartDate/phaseEndDate）＋
//      下一步 → getStudents(true)（:159-215：queryForm.valid() → 清 phaseStart/End →
//      form/batch/getFormStudentListAjax → studentsModal（步驟：選取學員；標題＝
//      『階段名稱：<phaseTarget>＋表單名稱：<formTemplate>』:196-200）＋nextStepBtn 依
//      formTargetObj.formTargetTypeTag 分流（Form360→showImport360；其餘 showFormFlow，:201-206））。
//   2. studentsModal（:650-710）：訓練期間 tStartDate/tEndDate（modal 查詢＝getStudents(false)，
//      未輸入訓練期間! :172-176）＋studentsTable 4 欄（勾選/臨床訓練計畫 planTargetName/
//      訓練階段期間 phaseStartDateString~phaseEndDateString/學員姓名，:131-150）＋stu-select-all
//      （:153-158）＋下一步（collectStudents :227-246 → 未選擇學員! :219-222/:250-253）。
//   3a. flowModal（步驟：指定關卡及應完成日期，:711-747）：應完成日期 dueDate＋flowTable
//      （欄＝#／要發單學員／流程N(roleName)，genFlowTableHead :281-296；學員關自動帶學員名、
//      其餘關＝人員下拉（templateForAny :749-757，legacy 另帶 searchAcctCode 搜尋））＋
//      送出表單 → saveForm（:446-518）：dueDate 空→『未設定應完成日期!』；<today→『表單應完成日
//      需大於系統時間!』；第一關（index==2＝第一個流程欄）accountId==-1→『必須指定第一關卡之
//      流程人員!』→ form/batch/saveBathFormAjax（payload formList/formTargetId/
//      formTemplateVersion/dueDate）→ 清表＋hide＋showInfoDialog(desc)。
//   3b. 360FormModal（步驟：上傳評核者名單，:758-818）：dueDate360＋targetDate（評核日期標記）＋
//      是否排除學員本身＋empFile(.xlsx) → 送出表單 saveForm360（:520-560）：360FormForm.valid()
//      → <today alert → FormData(empFile) → form/batch/saveBath360FormAjax → info(desc)。
//   頁面另鈕：360度評核者匯入範本下載 → downloadTemplate('360TemplateXLXS')（Blob 範本先例）。
//
// 誠實標註（D23-x，KNOWN-NOT-DONE 批次 23 節）：
//   - formTargetObj/flowList/學員清單＝種子常數（BATCH_SEND_TEMPLATE_OPTIONS/
//     BATCH_SEND_FLOW_VERSION/BATCH_SEND_STUDENTS）【雛形值待實機校準】（legacy 由
//     option data-all＋getFormStudentListAjax 回應帶回）。
//   - 流程人員選項＝教師池平列（legacy 依 flowRole 分 clinical/deptAdmin/deptManager/director
//     池並預帶 stu.clinicalName 等預設；B2-1 級聯→池平列先例）【雛形值待實機校準】；
//     searchAcctCode 搜尋框不鏡像（SearchableSelect 未做，Select 原生語意）。
//   - today＝server ${today} → fixtures DATE_EPOCH '2026-01-05'（不用 Date.now()；批次 13 先例）。
//   - saveForm/saveForm360 端點未映射 → dataSource.update 誠實標註（D21-4 同款）；empFile
//     檔案不落地（B2-1 先例，存檔名）。
//   - modal 查詢（getStudents(false)）與頁面下一步都打 getFormStudentListAjax（list op）；
//     complexBodySubmitId 自報＝頁面下一步（主要入口）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Checkbox } from '@/components/base/Checkbox';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  BATCH_SEND_FLOW_VERSION,
  BATCH_SEND_TEMPLATE_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  COURSE_CLAZZ_PHASE_OPTIONS,
  EXAM_DISPATCH_TEACHER_OPTIONS,
} from '@/mocks/resource-rules';
import { legacyAlertFallback, downloadTemplateBlob } from '../templates/page-behaviors';

/** fixtures 固定今天（planFormBatchSend.jsp:10 `var today='${today}'`；批次 13 先例）。 */
const TODAY = '2026-01-05';

const FLOW_LIST = BATCH_SEND_FLOW_VERSION.formTemplateVersion.flowList;

export function PlanFormBatchSendBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數
  // （規格 §7.1；fields 陣列驅動——陣列處包 override，渲染處 placeholder 條件化；
  // override 命中含空陣列即移除 placeholder 空選項）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // 查詢表單（8 required select；required inline＝jquery.valid() 鏡像【雛形值待實機校準】）。
  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobTypeId, setJobTypeId] = useState(ACCOUNT_JOB_TYPE_OPTIONS[0]!.value);
  const [departmentId, setDepartmentId] = useState(ACCOUNT_DEPARTMENT_OPTIONS[0]!.value);
  const [planTargetId, setPlanTargetId] = useState('');
  const [phaseDepartmentId, setPhaseDepartmentId] = useState('');
  const [phaseTargetId, setPhaseTargetId] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // studentsModal（步驟：選取學員）。
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [students, setStudents] = useState<readonly Row[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [tStartDate, setTStartDate] = useState('');
  const [tEndDate, setTEndDate] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // flowModal／360FormModal。
  const [flowOpen, setFlowOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [flowSelections, setFlowSelections] = useState<Record<string, string>>({});
  const [form360Open, setForm360Open] = useState(false);
  const [dueDate360, setDueDate360] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [excludeSelf, setExcludeSelf] = useState('1');
  const [empFileName, setEmpFileName] = useState('');
  const [form360Errors, setForm360Errors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /** 選中表單的 formTargetTypeTag（legacy option data-all；:201-206 分流）。 */
  const formTargetTypeTag =
    BATCH_SEND_TEMPLATE_OPTIONS.find((option) => option.value === formTemplateId)?.formTargetTypeTag ?? 'Flow';
  const selectedFormLabel = BATCH_SEND_TEMPLATE_OPTIONS.find((option) => option.value === formTemplateId)?.label ?? '';
  const selectedPhaseLabel =
    COURSE_CLAZZ_PHASE_OPTIONS.find((option) => option.value === phaseTargetId)?.label ?? '';

  /** 查詢表單 required inline（queryForm.valid() 鏡像；8 欄）。 */
  function validateQueryForm(): boolean {
    const requireds: readonly [string, string][] = [
      ['annualId', annualId],
      ['organizationId', organizationId],
      ['jobTypeId', jobTypeId],
      ['departmentId', departmentId],
      ['planTargetId', planTargetId],
      ['phaseDepartmentId', phaseDepartmentId],
      ['phaseTargetId', phaseTargetId],
      ['formTemplateId', formTemplateId],
    ];
    const errors: Record<string, string> = {};
    for (const [name, value] of requireds) {
      if (value === '') {
        errors[name] = '必填欄位';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  /** getStudents（:159-215）。cleanDate=true＝頁面下一步（清訓練期間）；false＝modal 查詢。 */
  async function getStudents(cleanDate: boolean): Promise<void> {
    if (cleanDate) {
      if (!validateQueryForm()) {
        return;
      }
    } else {
      if (tStartDate === '' || tEndDate === '') {
        await dialogs.alert('未輸入訓練期間!');
        return;
      }
    }
    setBusy(true);
    try {
      // form/batch/getFormStudentListAjax（:179；list op）；filters 直送（C5-h；
      // server 端過濾語意＝種子直出 D23-3 同款）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: {
          annualId,
          organizationId,
          jobTypeId,
          departmentId,
          planTargetId,
          phaseDepartmentId,
          phaseTargetId,
          formTemplateId,
          phaseStartDate: cleanDate ? '' : tStartDate,
          phaseEndDate: cleanDate ? '' : tEndDate,
        },
      });
      setStudents(result.rows);
      setSelected(new Set());
      setModalTitle(`階段名稱：${selectedPhaseLabel}\n表單名稱：${selectedFormLabel}`);
      setStudentsOpen(true);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** collectStudents（:227-246）。 */
  function collectStudents(): readonly Row[] {
    return students.filter((row) => selected.has(String(row['studentId'] ?? '')));
  }

  /** modal 下一步（nextStepBtn :201-206/:250-253/:702-706）。 */
  function nextStep(): void {
    const picked = collectStudents();
    if (picked.length === 0) {
      void dialogs.alert('未選擇學員!');
      return;
    }
    setStudentsOpen(false);
    if (formTargetTypeTag === 'Form360') {
      setForm360Open(true);
    } else {
      setFlowOpen(true);
    }
  }

  /** saveForm（:446-518）。 */
  async function saveForm(): Promise<void> {
    if (dueDate === '') {
      await dialogs.alert('未設定應完成日期!');
      return;
    }
    if (dueDate < TODAY) {
      await dialogs.alert('表單應完成日需大於系統時間!');
      return;
    }
    // 第一關（index==2＝第一個流程欄，:478-480）：每個學員都要有人選。
    const firstFlow = FLOW_LIST[0]!;
    const missingFirst = collectStudents().some(
      (stu) => (flowSelections[`${String(stu['studentId'] ?? '')}:${firstFlow.id}`] ?? '') === '',
    );
    if (missingFirst) {
      await dialogs.alert('必須指定第一關卡之流程人員!');
      return;
    }
    const formList = collectStudents().map((stu) => ({
      studentId: String(stu['studentId'] ?? ''),
      flowList: FLOW_LIST.map((fw) => ({
        flowRole: fw.flowRole,
        formTemplateVersionFlowId: fw.id,
        sequence: fw.sequence,
        accountId:
          fw.flowRole === 'Student'
            ? String(stu['studentId'] ?? '')
            : (flowSelections[`${String(stu['studentId'] ?? '')}:${fw.id}`] || '-1'),
      })),
    }));
    setBusy(true);
    try {
      // form/batch/saveBathFormAjax（:490-505）；endpoint 未映射 → update 誠實標註（D21-4 同款）。
      // 寫入目標＝首筆學員列（mock update 需真列 id；payload mock 不消費，批次 21 同款）。
      const picked = collectStudents();
      await dataSource.update(pageId, String(picked[0]?.['id'] ?? ''), {
        formList,
        formTargetId: formTemplateId,
        formTemplateVersion: BATCH_SEND_FLOW_VERSION.formTemplateVersion,
        dueDate,
      });
      setFlowOpen(false);
      setFlowSelections({});
      setDueDate('');
      await dialogs.info('發送成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** saveForm360（:520-560）。 */
  async function saveForm360(): Promise<void> {
    // 360FormForm.valid()（:521）：dueDate360/targetDate/excludeSelf/empFile required inline。
    const errors: Record<string, string> = {};
    if (dueDate360 === '') errors.dueDate360 = '必填欄位';
    if (targetDate === '') errors.targetDate = '必填欄位';
    if (excludeSelf === '') errors.excludeSelf = '必填欄位';
    if (empFileName === '') errors.empFile = '必填欄位';
    setForm360Errors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    if (dueDate360 < TODAY) {
      await dialogs.alert('表單應完成日需大於系統時間!');
      return;
    }
    setBusy(true);
    try {
      // form/batch/saveBath360FormAjax（:539-557；FormData empFile）——檔案不落地（B2-1 先例）。
      // 寫入目標＝首筆學員列（mock update 需真列 id；payload mock 不消費）。
      const picked360 = collectStudents();
      await dataSource.update(pageId, String(picked360[0]?.['id'] ?? ''), {
        students: collectStudents().map((stu) => String(stu['studentId'] ?? '')),
        formTargetId: formTemplateId,
        dueDate360,
        targetDate,
        excludeSelf,
        empFile: empFileName,
      });
      setForm360Open(false);
      setEmpFileName('');
      setDueDate360('');
      setTargetDate('');
      await dialogs.info('發送成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const queryFields: readonly { name: string; label: string; value: string; set: (next: string) => void; options: readonly { value: string; label: string }[] }[] = [
    { name: 'annualId', label: '學年度：', value: annualId, set: setAnnualId, options: overrideOptions('annualId') ?? [{ value: '114', label: '114' }, { value: '115', label: '115' }] },
    { name: 'organizationId', label: '醫院：', value: organizationId, set: setOrganizationId, options: overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS },
    { name: 'jobTypeId', label: '職類：', value: jobTypeId, set: setJobTypeId, options: overrideOptions('jobTypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS },
    { name: 'departmentId', label: '科部：', value: departmentId, set: setDepartmentId, options: overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS },
    { name: 'planTargetId', label: '訓練計畫：', value: planTargetId, set: setPlanTargetId, options: overrideOptions('planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS },
    { name: 'phaseDepartmentId', label: '訓練階段部門：', value: phaseDepartmentId, set: setPhaseDepartmentId, options: overrideOptions('phaseDepartmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS },
    { name: 'phaseTargetId', label: '訓練階段名稱：', value: phaseTargetId, set: setPhaseTargetId, options: overrideOptions('phaseTargetId') ?? COURSE_CLAZZ_PHASE_OPTIONS },
    { name: 'formTemplateId', label: '訓練計畫表單：', value: formTemplateId, set: setFormTemplateId, options: overrideOptions('formTemplateId') ?? BATCH_SEND_TEMPLATE_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
  ];

  const studentColumns: readonly TableColumn[] = [
    { key: 'check', header: '', align: 'center' },
    { key: 'planTargetName', header: '臨床訓練計畫', align: 'center' },
    { key: 'phase', header: '訓練階段期間', align: 'center' },
    { key: 'studentName', header: '學員姓名', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="批次發送表單" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（planFormBatchSend.jsp:571-645）。 */}
        {/* 查詢表單（planFormBatchSend.jsp:571-645；hidden phaseStart/End 由 modal 訓練期間帶入）。 */}
        <input type="hidden" name="phaseStartDate" value="" readOnly />
        <input type="hidden" name="phaseEndDate" value="" readOnly />
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {queryFields.map((field) => (
            <div key={field.name} className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm" htmlFor={`${pageId}-query-${field.name}`}>
                <span className="text-danger">*</span>
                {field.label}
              </label>
              <div className="flex-1">
                <Select
                  uiId={`${pageId}-query-${field.name}`}
                  name={field.name}
                  value={field.value}
                  onChange={field.set}
                  options={field.options}
                  {...(overrideOptions(field.name) ? {} : { placeholder: '請選擇' })}
                />
                {fieldErrors[field.name] === undefined ? null : (
                  <div data-ui-id={`${pageId}-query-error-${field.name}`} className="text-danger text-xs">
                    {fieldErrors[field.name]}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="col-span-2 flex gap-2">
            {/* 下一步（:637 getStudents(true)）。 */}
            <Button uiId={`${pageId}-next-step-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void getStudents(true)}>
              下一步
            </Button>
            {/* 360度評核者匯入範本下載（:644 downloadTemplate('360TemplateXLXS')）；Blob 範本先例。 */}
            <Button
              uiId={`${pageId}-template-download-btn`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                downloadTemplateBlob('360TemplateXLXS.xlsx');
              }}
            >
              360度評核者匯入範本下載
            </Button>
          </div>
        </div>
      </ContentPanel>
      {/* studentsModal（步驟：選取學員，:650-710）。 */}
      {studentsOpen ? (
        <ModalFrame
          uiId={`${pageId}-students-modal`}
          open={true}
          title="步驟：選取學員"
          size="lg"
          onClose={() => {
            setStudentsOpen(false);
          }}
          footer={
            <>
              <Button uiId={`${pageId}-students-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => setStudentsOpen(false)}>
                關閉
              </Button>
              <Button uiId={`${pageId}-students-modal-next`} variant="primary" size="sm" type="button" onClick={nextStep}>
                下一步
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-students-modal-body`} className="space-y-3">
            <div data-ui-id={`${pageId}-students-modal-caption`} className="whitespace-pre-line text-sm font-bold">
              {modalTitle}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm" htmlFor={`${pageId}-students-modal-t-start-date`}>
                <span className="text-danger">*</span>
                訓練期間：
              </label>
              <TextInput uiId={`${pageId}-students-modal-t-start-date`} name="tStartDate" value={tStartDate} onChange={setTStartDate} maxLength={10} placeholder="YYYY-MM-DD" />
              <TextInput uiId={`${pageId}-students-modal-t-end-date`} name="tEndDate" value={tEndDate} onChange={setTEndDate} maxLength={10} placeholder="YYYY-MM-DD" />
              {/* modal 查詢（getStudents(false)，:680）。 */}
              <Button uiId={`${pageId}-students-modal-query`} variant="default" size="sm" type="button" disabled={busy} onClick={() => void getStudents(false)}>
                查詢
              </Button>
            </div>
            {/* studentsTable 4 欄（:131-150）＋stu-select-all（:153-158）。 */}
            <Checkbox
              uiId={`${pageId}-stu-select-all`}
              checked={students.length > 0 && selected.size === students.length}
              onChange={(checked) => {
                setSelected(checked ? new Set(students.map((row) => String(row['studentId'] ?? ''))) : new Set());
              }}
            />
            <Table
              uiId={`${pageId}-students-table`}
              columns={studentColumns}
              rows={students}
              rowKey={(row) => String(row['studentId'] ?? '').toLowerCase()}
              emptyText={t('table.empty')}
              renderCell={(row, columnKey) => {
                const studentId = String(row['studentId'] ?? '');
                if (columnKey === 'check') {
                  return (
                    <Checkbox
                      uiId={`${pageId}-students-table-row-${studentId.toLowerCase()}-check`}
                      checked={selected.has(studentId)}
                      onChange={(checked) => {
                        const next = new Set(selected);
                        if (checked) {
                          next.add(studentId);
                        } else {
                          next.delete(studentId);
                        }
                        setSelected(next);
                      }}
                    />
                  );
                }
                if (columnKey === 'phase') {
                  return `${String(row['phaseStartDateString'] ?? '')}~${String(row['phaseEndDateString'] ?? '')}`;
                }
                return String(row[columnKey] ?? '');
              }}
            />
          </div>
        </ModalFrame>
      ) : null}
      {/* flowModal（步驟：指定關卡及應完成日期，:711-747）。 */}
      {flowOpen ? (
        <ModalFrame
          uiId={`${pageId}-flow-modal`}
          open={true}
          title="步驟：指定關卡及應完成日期"
          size="lg"
          onClose={() => {
            setFlowOpen(false);
          }}
          footer={
            <>
              <Button uiId={`${pageId}-flow-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => setFlowOpen(false)}>
                關閉
              </Button>
              <Button
                uiId={`${pageId}-flow-modal-submit`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                // C5-j：送出真呼叫 dataSource.update（saveForm → saveBathFormAjax；未映射＝D21-4 同款）。
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  void saveForm();
                }}
              >
                送出表單
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-flow-modal-body`} className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm" htmlFor={`${pageId}-flow-modal-due-date`}>
                <span className="text-danger">*</span>
                應完成日期：
              </label>
              <TextInput uiId={`${pageId}-flow-modal-due-date`} name="dueDate" value={dueDate} onChange={setDueDate} maxLength={10} placeholder="YYYY-MM-DD" />
            </div>
            {/* flowTable（genFlowTableHead :281-296：#／要發單學員／流程N(roleName)；學員關自動帶名，:302-306）。 */}
            <Table
              uiId={`${pageId}-flow-table`}
              columns={[
                { key: 'no', header: '#', align: 'center' },
                { key: 'student', header: '要發單學員', align: 'center' },
                ...FLOW_LIST.map((fw, index) => ({
                  key: `flow-${fw.id}`,
                  header: `流程${index + 1}(${fw.roleName})`,
                  align: 'center' as const,
                })),
              ]}
              rows={collectStudents()}
              rowKey={(row) => String(row['studentId'] ?? '').toLowerCase()}
              emptyText={t('table.empty')}
              renderCell={(row, columnKey) => {
                const studentId = String(row['studentId'] ?? '');
                if (columnKey === 'no') {
                  return String((collectStudents().findIndex((s) => String(s['studentId'] ?? '') === studentId) ?? 0) + 1);
                }
                if (columnKey === 'student') {
                  // 第一個 Student 關的欄（genFlowTableBody :302-306：flowRole==='Student' → 學員名）。
                  return studentNameOf(row);
                }
                const flowId = columnKey.replace('flow-', '');
                const flow = FLOW_LIST.find((fw) => fw.id === flowId);
                if (flow === undefined) {
                  return '';
                }
                if (flow.flowRole === 'Student') {
                  // 學員關自動帶學員名（:302-306）。
                  return studentNameOf(row);
                }
                return (
                  <Select
                    uiId={`${pageId}-flow-table-row-${studentId.toLowerCase()}-${flowId}`}
                    value={flowSelections[`${studentId}:${flowId}`] ?? ''}
                    onChange={(next) => {
                      setFlowSelections((current) => ({ ...current, [`${studentId}:${flowId}`]: next }));
                    }}
                    options={EXAM_DISPATCH_TEACHER_OPTIONS}
                    placeholder="請選擇"
                  />
                );
              }}
            />
          </div>
        </ModalFrame>
      ) : null}
      {/* 360FormModal（步驟：上傳評核者名單，:758-818）。 */}
      {form360Open ? (
        <ModalFrame
          uiId={`${pageId}-form360-modal`}
          open={true}
          title="步驟：上傳評核者名單"
          size="lg"
          onClose={() => {
            setForm360Open(false);
          }}
          footer={
            <>
              <Button uiId={`${pageId}-form360-modal-footer-close`} variant="default" size="sm" type="button" onClick={() => setForm360Open(false)}>
                關閉
              </Button>
              <Button
                uiId={`${pageId}-form360-modal-submit`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  void saveForm360();
                }}
              >
                送出表單
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-form360-modal-body`} className="space-y-3">
            {(
              [
                ['dueDate360', '應完成日期：', dueDate360, setDueDate360],
                ['targetDate', '評核日期標記：', targetDate, setTargetDate],
              ] as const
            ).map(([name, label, value, set]) => (
              <div key={name} className="flex items-center gap-2">
                <label className="w-32 shrink-0 text-sm" htmlFor={`${pageId}-form360-modal-${name}`}>
                  <span className="text-danger">*</span>
                  {label}
                </label>
                <div className="flex-1">
                  <TextInput uiId={`${pageId}-form360-modal-${name}`} name={name} value={value} onChange={set} maxLength={10} placeholder="YYYY-MM-DD" />
                  {form360Errors[name] === undefined ? null : (
                    <div data-ui-id={`${pageId}-form360-modal-error-${name}`} className="text-danger text-xs">
                      {form360Errors[name]}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm">
                <span className="text-danger">*</span>
                是否排除學員本身：
              </label>
              <Select
                uiId={`${pageId}-form360-modal-exclude-self`}
                value={excludeSelf}
                onChange={setExcludeSelf}
                options={[
                  { value: '1', label: '是' },
                  { value: '0', label: '否' },
                ]}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-sm" htmlFor={`${pageId}-form360-modal-emp-file`}>
                <span className="text-danger">*</span>
                評核者名單：
              </label>
              <div className="flex-1">
                <TextInput
                  uiId={`${pageId}-form360-modal-emp-file`}
                  name="empFile"
                  value={empFileName}
                  onChange={setEmpFileName}
                  placeholder=".xlsx 檔名（雛形文字輸入；檔案不落地 B2-1 先例）"
                />
                {form360Errors.empFile === undefined ? null : (
                  <div data-ui-id={`${pageId}-form360-modal-error-emp-file`} className="text-danger text-xs">
                    {form360Errors.empFile}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalFrame>
      ) : null}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

function studentNameOf(row: Row): string {
  return String(row['studentName'] ?? '');
}
